document.addEventListener("DOMContentLoaded", () => {
  if (!document.getElementById("nfi-total-price-value")) return;

  //================================================================================
  // Formatting / helpers
  //================================================================================

  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        const fmt = window.Shopify.money_format || "${{amount}}";
        return window.Shopify.formatMoney(Number(cents || 0), fmt);
      }
    } catch (_) {}

    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  function setTotal(cents, parts) {
    const totalEl = document.getElementById("nfi-total-price-value");
    const breakdownEl = document.getElementById("nfi-total-price-breakdown");

    if (!totalEl) return;

    totalEl.textContent = formatMoney(cents);

    if (!breakdownEl) return;

    if (!parts || parts.length === 0) {
      breakdownEl.style.display = "none";
      breakdownEl.textContent = "";
      return;
    }

    breakdownEl.style.display = "block";
    breakdownEl.innerHTML = parts.map(x => `<div>${x}</div>`).join("");
  }

  function getQuantityInput() {
    return (
      document.querySelector("quantity-selector input[name='quantity']") ||
      document.querySelector("input[name='quantity']")
    );
  }

  function getQuantity() {
    const q = Number(getQuantityInput()?.value);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  function isQuantityInput(target) {
    return target?.name === "quantity" && target === getQuantityInput();
  }

  //================================================================================
  // Price NEW override helper
  //================================================================================

  // Queried on every call: the block is replaced on shopify:section:load in the editor.
  function getTotalRoot() {
    return document.querySelector(".nfi-total-price");
  }

  function toPositiveCents(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  }

  // Reads a { "<variant_id>": <cents> } map from a data attribute on the Total block.
  function readVariantMap(attr) {
    const raw = getTotalRoot()?.getAttribute(attr);
    if (!raw) return {};

    try {
      const map = JSON.parse(raw);
      return map && typeof map === "object" ? map : {};
    } catch (_) {
      return {};
    }
  }

  // Variant value wins, then product value, otherwise null.
  function resolveCents(variantMap, productCents, variantId) {
    const variantCents = variantId != null ? toPositiveCents(variantMap[String(variantId)]) : null;
    return variantCents ?? toPositiveCents(productCents);
  }

  // "Price NEW": variant metafield (data-price-new-variants), then the product one
  // from the hidden input rendered by Liquid. Values are in cents.
  // Returns the value in cents, or null if not present / invalid.
  function getPriceNewCents(variantId) {
    const productCents = document.getElementById("nffi-price-new-cents")?.value;
    return resolveCents(readVariantMap("data-price-new-variants"), productCents, variantId);
  }

  // Ready to Carry bag + holster bundle discount (custom.bag_holster_bundle_discount), in cents.
  // Attributes are rendered only for products tagged "Show in Ready to Carry" + "BAGS WITH HOLSTER".
  function getBundleDiscountCents(variantId) {
    const productCents = getTotalRoot()?.getAttribute("data-bundle-discount-product");
    return resolveCents(readVariantMap("data-bundle-discount-variants"), productCents, variantId);
  }

  // Read the holster color dropdown's currently selected option and compute the
  // upcharge in cents. The option elements expose their upcharge via `data-price`
  // in DOLLARS (e.g. data-price="20" means +$20). The base option is the one with
  // the smallest data-price value.
  // Returns { upchargeCents, label } or null if no relevant upcharge.
  function getHolsterColorUpcharge() {
    const select = document.getElementById("holster-color");
    if (!select) return null;

    const allOptions = Array.from(select.options || []);
    const optionsWithPrice = allOptions
      .map(opt => {
        const p = Number(opt.getAttribute("data-price"));
        return Number.isFinite(p) ? { opt, price: p } : null;
      })
      .filter(Boolean);

    if (optionsWithPrice.length === 0) return null;

    const basePrice = Math.min(...optionsWithPrice.map(o => o.price));
    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return null;

    const selectedPrice = Number(selectedOption.getAttribute("data-price"));
    if (!Number.isFinite(selectedPrice)) return null;

    const upchargeDollars = selectedPrice - basePrice;
    if (upchargeDollars <= 0) return null;

    return {
      upchargeCents: Math.round(upchargeDollars * 100),
      label: selectedOption.textContent.trim() || "Holster upgrade"
    };
  }

  //================================================================================
  // Context API (single source of truth)
  //================================================================================

  function getCtx() {
    // Prefer the shared context from custom.product-info.js
    if (window.NFFI_Product && typeof window.NFFI_Product.getContext === "function") {
      return window.NFFI_Product.getContext();
    }

    return null;
  }

  //================================================================================
  // Modes (should mirror custom.product-info.js)
  //================================================================================

  const PRODUCT_MODES = {
    NONE: "NONE",
    LEGACY_BUNDLE_BAG_HLST: "LEGACY_BUNDLE_BAG_HLST",
    LEGACY_BAG_WITH_HOLSTER: "LEGACY_BAG_WITH_HOLSTER",
    LEGACY_BAG_WITH_ADAPTER: "LEGACY_BAG_WITH_ADAPTER",
    STAND_ALONE_BAG: "STAND_ALONE_BAG",
    STANDALONE_HOLSTER: "STANDALONE_HOLSTER",
    CHEST_HOLSTER: "CHEST_HOLSTER",
    BAG_OPTIONS_BAG_ONLY: "BAG_OPTIONS_BAG_ONLY",
    BAG_OPTIONS_BAG_WITH_HOLSTER: "BAG_OPTIONS_BAG_WITH_HOLSTER",
    BAG_OPTIONS_BAG_WITH_ADAPTER: "BAG_OPTIONS_BAG_WITH_ADAPTER"
  };

  function getBagConfig() {
    const checked = document.querySelector("input[name='bag-config-ui']:checked");
    return checked ? checked.value : null;
  }

  function getProductMode(ctx) {
    if (!ctx) return PRODUCT_MODES.NONE;

    const {
      isBag,
      isHolster,
      isChestHolster,
      isLegacyBagWithHolster,
      isLegacyBagWithAdapter,
      isBundleBagHlstSingle,
      bagOptionsEnabled
    } = ctx;

    if (isBundleBagHlstSingle && !bagOptionsEnabled) {
      return PRODUCT_MODES.LEGACY_BUNDLE_BAG_HLST;
    }

    if (isLegacyBagWithHolster && !bagOptionsEnabled) {
      return PRODUCT_MODES.LEGACY_BAG_WITH_HOLSTER;
    }

    if (isLegacyBagWithAdapter && !bagOptionsEnabled) {
      return PRODUCT_MODES.LEGACY_BAG_WITH_ADAPTER;
    }

    if (isChestHolster && !isBag) {
      return PRODUCT_MODES.CHEST_HOLSTER;
    }

    if (isHolster && !isBag) {
      return PRODUCT_MODES.STANDALONE_HOLSTER;
    }

    if (isBag && bagOptionsEnabled) {
      // IMPORTANT: no default "BAG_ONLY" here; cfg is only what user selected
      const cfg = getBagConfig();

      if (cfg === "BAG_WITH_HOLSTER") return PRODUCT_MODES.BAG_OPTIONS_BAG_WITH_HOLSTER;
      if (cfg === "BAG_WITH_ADAPTER") return PRODUCT_MODES.BAG_OPTIONS_BAG_WITH_ADAPTER;
      return PRODUCT_MODES.BAG_OPTIONS_BAG_ONLY;
    }

    if (isBag && !bagOptionsEnabled) {
      return PRODUCT_MODES.STAND_ALONE_BAG;
    }

    return PRODUCT_MODES.NONE;
  }

  //================================================================================
  // AutoProducts / Addons
  //================================================================================

  function getAutoProductsMeta(ctx) {
    const ap = ctx?.addons?.AutoProducts;
    if (!Array.isArray(ap) || ap.length === 0) return [];

    return ap.reduce((acc, p) => {
      const v = Array.isArray(p.variants) ? p.variants[0] : null;
      if (!v || v.available === false) return acc;

      const cents = Number(v.price);
      if (!Number.isFinite(cents)) return acc;

      acc.push({
        title: p.title || "Add-on",
        price: cents
      });

      return acc;
    }, []);
  }

  function getSelectedAddonBlocksMeta() {
    const selected = document.querySelectorAll(".addon-block.selected");
    const result = [];

    selected.forEach(el => {
      const cents = Number(el.getAttribute("data-addon-price-cents"));
      if (!Number.isFinite(cents)) return;

      const title =
        el.getAttribute("data-addon-title") ||
        el.querySelector(".addon-block__title")?.textContent.trim() ||
        "Add-on";

      result.push({ title, price: cents });
    });

    return result;
  }

  //================================================================================
  // Core: recalcTotal – must mirror getProductItems()
  //================================================================================

  function recalcTotal() {
    const ctx = getCtx();
    console.log("[NFFI] recalcTotal context:", ctx);
    if (!ctx || !ctx.product) return;

    const mode = getProductMode(ctx);

    const {
      product,
      variant,
      bagMetaVariant,
      adapterMetaVariant,
      addons,
      isBag,
      isHolster,
      isChestHolster,
      isBundleBagHlstSingle,
      isLegacyBagWithHolster,
      isLegacyBagWithAdapter,
      hasAutoProducts
    } = ctx;

    const qty = getQuantity();
    const parts = [];
    let totalPerUnit = 0;

    // If Bag Options UI exists and user hasn't chosen a mode -> Total must be 0
    const bagConfigRadios = document.querySelectorAll("input[name='bag-config-ui']");
    const cfgRaw = getBagConfig();

    if (
      ctx.isBag &&
      ctx.bagOptionsEnabled &&
      !ctx.isBundleBagHlstSingle &&
      !ctx.isLegacyBagWithHolster &&
      !ctx.isLegacyBagWithAdapter &&
      bagConfigRadios.length > 0 &&
      !cfgRaw
    ) {
      setTotal(0, []);
      return;
    }

    //================================================================================
    // OVERRIDE: Price NEW metafield (read from hidden input rendered by Liquid)
    // If the product has a "Price NEW" set, the bundle is on sale and we use that
    // as the authoritative bundle price instead of summing component prices.
    // We still add the upcharge for holster color upgrades ("+ $20") by reading
    // the data-price attribute on the currently selected option in the holster
    // color dropdown. Selected addon blocks (Strap Extender, etc.) are also
    // added on top.
    //================================================================================

    const priceNewCents = getPriceNewCents(variant?.id);

    if (priceNewCents != null) {
      let overrideTotal = priceNewCents;
      const overrideParts = [];

      const bundleTitle = product?.title || "Product";
      overrideParts.push(`${bundleTitle}: ${formatMoney(priceNewCents)}`);

      // Holster color upcharge (reads data-price from selected <option>)
      const holsterUpcharge = getHolsterColorUpcharge();
      if (holsterUpcharge) {
        overrideTotal += holsterUpcharge.upchargeCents;
        overrideParts.push(
          `${holsterUpcharge.label}: +${formatMoney(holsterUpcharge.upchargeCents)}`
        );
      }

      // Add user-selected addon blocks (Strap Extender, Shoulder Padding, etc.)
      getSelectedAddonBlocksMeta().forEach(addon => {
        overrideTotal += addon.price;
        overrideParts.push(`${addon.title}: ${formatMoney(addon.price)}`);
      });

      overrideParts.push(`Qty: ${qty}`);
      setTotal(overrideTotal * qty, overrideParts);
      return;
    }

    //================================================================================
    // FALLBACK: per-component calculation (used when Price NEW is not set)
    //================================================================================

    // Main line: BAG metafield wins if allowed
    const mainVariant = ctx.canUseBagMetaAsMain && bagMetaVariant ? bagMetaVariant : variant;

    const mainPriceCents = Number(mainVariant?.price) || 0;

    let mainTitle = product?.title || "";
    if (ctx.canUseBagMetaAsMain && addons?.BAG?.title) {
      mainTitle = addons.BAG.title;
    }

    // LEGACY_BUNDLE_BAG_HLST: one bundle line + autoproducts + addons
    if (mode === PRODUCT_MODES.LEGACY_BUNDLE_BAG_HLST) {
      totalPerUnit += mainPriceCents;
      if (mainTitle) parts.push(`${mainTitle}: ${formatMoney(mainPriceCents)}`);

      // AutoProducts
      getAutoProductsMeta(ctx).forEach(ap => {
        totalPerUnit += ap.price;
        parts.push(`${ap.title}: ${formatMoney(ap.price)}`);
      });

      // addon blocks
      getSelectedAddonBlocksMeta().forEach(addon => {
        totalPerUnit += addon.price;
        parts.push(`${addon.title}: ${formatMoney(addon.price)}`);
      });

      const total = totalPerUnit * qty;
      parts.push(`Qty: ${qty}`);
      setTotal(total, parts);
      return;
    }

    // LEGACY_BAG_WITH_ADAPTER: single combined product line
    if (mode === PRODUCT_MODES.LEGACY_BAG_WITH_ADAPTER) {
      //single combined product line
      if (!(ctx.canUseBagMetaAsMain
        && bagMetaVariant
        && bagMetaVariant.available !== false
        && adapterMetaVariant
        && adapterMetaVariant.available !== false)) {

        totalPerUnit += mainPriceCents;
        if (mainTitle) parts.push(`${mainTitle}: ${formatMoney(mainPriceCents)}`);

        // addon blocks
        getSelectedAddonBlocksMeta().forEach(addon => {
          totalPerUnit += addon.price;
          parts.push(`${addon.title}: ${formatMoney(addon.price)}`);
        });

        const total = totalPerUnit * qty;
        parts.push(`Qty: ${qty}`);
        setTotal(total, parts);
        return;
      }


      //2 products (bag + adapter)
      let adapterPrice = 0;
      let adapterTitle = addons?.["IWB Adapter"]?.title || "";

      if (adapterMetaVariant) {
        const n = Number(adapterMetaVariant.price);
        if (Number.isFinite(n)) adapterPrice = n;
      }

      if (adapterPrice > 0) {
        totalPerUnit += adapterPrice;
        if (adapterTitle) parts.push(`${adapterTitle}: ${formatMoney(adapterPrice)}`);
      }

    }

    // Base line for all other modes
    totalPerUnit += mainPriceCents;
    if (mainTitle) parts.push(`${mainTitle}: ${formatMoney(mainPriceCents)}`);

    // Ready to Carry: bag + holster bundle discount on the child bag line
    const isReadyToCarry =
      ctx.tags?.includes("Show in Ready to Carry") && ctx.tags?.includes("BAGS WITH HOLSTER");

    if (
      isReadyToCarry &&
      mode === PRODUCT_MODES.LEGACY_BAG_WITH_HOLSTER &&
      ctx.canUseBagMetaAsMain &&
      bagMetaVariant
    ) {
      const discount = Math.min(getBundleDiscountCents(variant?.id) || 0, mainPriceCents);

      if (discount > 0) {
        totalPerUnit -= discount;
        const label = getTotalRoot()?.getAttribute("data-bundle-discount-label") || "Bundle discount";
        parts.push(`${label}: −${formatMoney(discount)}`);
      }
    }

    // BAG_WITH_ADAPTER (Bag Options split)
    if (mode === PRODUCT_MODES.BAG_OPTIONS_BAG_WITH_ADAPTER) {
      let adapterPrice = 0;
      let adapterTitle = addons?.["IWB Adapter"]?.title || "";

      const adapterPriceInput = document.querySelector("#selected-adapter-price-cents");
      if (adapterPriceInput) {
        const n = Number(adapterPriceInput.value);
        if (Number.isFinite(n) && n > 0) adapterPrice = n;
      }

      if (!adapterPrice && adapterMetaVariant) {
        const n = Number(adapterMetaVariant.price);
        if (Number.isFinite(n)) adapterPrice = n;
      }

      if (adapterPrice > 0) {
        totalPerUnit += adapterPrice;
        if (adapterTitle) parts.push(`${adapterTitle}: ${formatMoney(adapterPrice)}`);
      }
    }

    // CHEST_HOLSTER
    if (
      mode === PRODUCT_MODES.CHEST_HOLSTER
    ) {
      const holsterPriceEl = document.querySelector("#selected-holster-price-cents");
      let holsterPrice = 0;

      if (holsterPriceEl) {
        const n = Number(holsterPriceEl.value);
        if (Number.isFinite(n) && n > 0) holsterPrice = n;
      }

      if (holsterPrice > 0) {
        const holsterTitle =
          document.querySelector("#selected-holster-name")?.value || "";

        totalPerUnit += holsterPrice;
        if (holsterTitle) parts.push(`${holsterTitle}: ${formatMoney(holsterPrice)}`);
      }
    }

    // BAG_WITH_HOLSTER (legacy + Bag Options)
    if (
      mode === PRODUCT_MODES.LEGACY_BAG_WITH_HOLSTER ||
      mode === PRODUCT_MODES.BAG_OPTIONS_BAG_WITH_HOLSTER
    ) {
      const holsterPriceEl = document.querySelector("#selected-holster-price-cents");
      let holsterPrice = 0;

      if (holsterPriceEl) {
        const n = Number(holsterPriceEl.value);
        if (Number.isFinite(n) && n > 0) holsterPrice = n;
      }

      if (holsterPrice > 0) {
        const holsterTitle =
          document.querySelector("#selected-holster-name")?.value || "";

        totalPerUnit += holsterPrice;
        if (holsterTitle) parts.push(`${holsterTitle}: ${formatMoney(holsterPrice)}`);
      }
    }

    // AutoProducts
    getAutoProductsMeta(ctx).forEach(ap => {
      totalPerUnit += ap.price;
      parts.push(`${ap.title}: ${formatMoney(ap.price)}`);
    });

    // addon blocks
    getSelectedAddonBlocksMeta().forEach(addon => {
      totalPerUnit += addon.price;
      parts.push(`${addon.title}: ${formatMoney(addon.price)}`);
    });

    const total = totalPerUnit * qty;
    parts.push(`Qty: ${qty}`);
    setTotal(total, parts);
  }

  //================================================================================
  // Events / bindings
  //================================================================================

  document.addEventListener("input", event => {
    if (isQuantityInput(event.target)) recalcTotal();
  });

  document.addEventListener("change", event => {
    const target = event.target;

    if (
      isQuantityInput(target) ||
      target.matches?.('form[action$="/cart/add"] [name="id"]') ||
      target.id === "holster-color"
    ) {
      recalcTotal();
    }
  });

  document.addEventListener("click", event => {
    if (event.target.closest?.("quantity-selector .quantity-selector__button")) {
      setTimeout(recalcTotal, 0);
    }
  });

  document.addEventListener("variant:change", recalcTotal);

  document.addEventListener("shopify:section:load", recalcTotal);

  // External triggers (from firearm-options.js / bag-options.js)
  document.addEventListener("nffi:product:changed", recalcTotal);
  document.addEventListener("nffi:bag-options:changed", recalcTotal);
  document.addEventListener("nffi:holster-selected", recalcTotal);
  document.addEventListener("nffi:adapter-selected", recalcTotal);
  document.addEventListener("nffi:total:recalc", recalcTotal);

  // Initial
  recalcTotal();

  // Expose minimal API
  window.NFFI_TotalPrice = {
    recalc: recalcTotal
  };
});